/**
 * Обёртка над официальным Telegram Mini Apps SDK (window.Telegram.WebApp).
 * Все вызовы безопасны вне Telegram (в обычном браузере при локальной разработке).
 * initDataUnsafe используется ТОЛЬКО для UI-подсказок; личность подтверждает сервер по initData.
 */

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface BottomButton {
  setText(t: string): BottomButton;
  show(): BottomButton;
  hide(): BottomButton;
  enable(): BottomButton;
  disable(): BottomButton;
  showProgress(leaveActive?: boolean): BottomButton;
  hideProgress(): BottomButton;
  onClick(cb: () => void): BottomButton;
  offClick(cb: () => void): BottomButton;
}

interface WebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { language_code?: string } };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  isVersionAtLeast(v: string): boolean;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor?(c: string): void;
  setBackgroundColor?(c: string): void;
  setBottomBarColor?(c: string): void;
  disableVerticalSwipes?(): void;
  openLink(url: string, opts?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  showConfirm(message: string, cb: (ok: boolean) => void): void;
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  MainButton: BottomButton;
  HapticFeedback?: {
    impactOccurred(s: 'light' | 'medium' | 'heavy'): void;
    notificationOccurred(t: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  onEvent(e: string, cb: () => void): void;
  offEvent(e: string, cb: () => void): void;
  /** Окно оплаты (Bot API 6.1+): статус paid | cancelled | failed | pending. */
  openInvoice?(url: string, cb?: (status: InvoiceStatus) => void): void;
  /** Отправка подготовленного ботом сообщения в выбранный чат (Bot API 8.0+). */
  shareMessage?(msgId: string, cb?: (sent: boolean) => void): void;
}

export type InvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending';

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp };
  }
}

export function webApp(): WebApp | null {
  const wa = window.Telegram?.WebApp;
  // Скрипт SDK загружается и вне Telegram, но initData тогда пустая.
  return wa && wa.initData ? wa : null;
}

export const isInTelegram = () => webApp() !== null;
export const getInitData = () => webApp()?.initData ?? '';
export const getStartParam = () => webApp()?.initDataUnsafe.start_param ?? null;
export const getTelegramLanguage = () => webApp()?.initDataUnsafe.user?.language_code ?? navigator.language;

export function initTelegram() {
  const wa = webApp();
  if (!wa) return;
  wa.ready();
  wa.expand();
  try {
    if (wa.isVersionAtLeast('7.7')) wa.disableVerticalSwipes?.();
  } catch {
    /* старые клиенты */
  }
  applyTheme();
  wa.onEvent('themeChanged', applyTheme);
}

/** Тема: используем цвета Telegram, если они заданы; иначе — собственная палитра. */
export function applyTheme() {
  const wa = webApp();
  const root = document.documentElement;
  const scheme = wa?.colorScheme ?? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  root.dataset.theme = scheme;
  const tp = wa?.themeParams;
  if (tp?.bg_color) root.style.setProperty('--tg-bg', tp.bg_color);
  if (tp?.secondary_bg_color) root.style.setProperty('--tg-secondary-bg', tp.secondary_bg_color);
  if (tp?.text_color) root.style.setProperty('--tg-text', tp.text_color);
  if (tp?.hint_color) root.style.setProperty('--tg-hint', tp.hint_color);
  try {
    wa?.setHeaderColor?.(scheme === 'dark' ? '#0a0e0d' : '#ffffff');
    wa?.setBackgroundColor?.(scheme === 'dark' ? '#0a0e0d' : '#f4f6f3');
    wa?.setBottomBarColor?.(scheme === 'dark' ? '#0a0e0d' : '#ffffff');
  } catch {
    /* метод недоступен в старых версиях */
  }
}

export const haptic = {
  tap: () => webApp()?.HapticFeedback?.impactOccurred('light'),
  success: () => webApp()?.HapticFeedback?.notificationOccurred('success'),
  error: () => webApp()?.HapticFeedback?.notificationOccurred('error'),
  select: () => webApp()?.HapticFeedback?.selectionChanged(),
};

/** Открыть t.me-ссылку внутри Telegram (профиль продавца, шаринг). */
export function openTelegramLink(url: string) {
  const wa = webApp();
  if (wa && url.startsWith('https://t.me/')) wa.openTelegramLink(url);
  else window.open(url, '_blank', 'noopener');
}

export function openExternal(url: string) {
  const wa = webApp();
  if (wa) wa.openLink(url);
  else window.open(url, '_blank', 'noopener');
}

export function confirmDialog(message: string): Promise<boolean> {
  const wa = webApp();
  if (wa) return new Promise((resolve) => wa.showConfirm(message, resolve));
  return Promise.resolve(window.confirm(message));
}

/** Кнопка «Назад» Telegram, синхронизированная с роутером. */
export function setBackButton(onBack: (() => void) | null) {
  const wa = webApp();
  if (!wa) return () => undefined;
  if (!onBack) {
    wa.BackButton.hide();
    return () => undefined;
  }
  wa.BackButton.show();
  wa.BackButton.onClick(onBack);
  return () => {
    wa.BackButton.offClick(onBack);
    wa.BackButton.hide();
  };
}

/** Ссылка «Поделиться» через нативный диалог выбора чата Telegram. */
export function shareToTelegram(url: string, text: string) {
  const share = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  openTelegramLink(share);
}

/** Telegram умеет пересылать подготовленные ботом сообщения-карточки (Bot API 8.0+). */
export function canShareMessage(): boolean {
  const wa = webApp();
  return Boolean(wa?.shareMessage && wa.isVersionAtLeast('8.0'));
}

/** Выбор чата и отправка подготовленного сообщения. true — отправлено, false — пользователь закрыл окно. */
export function shareMessage(msgId: string): Promise<boolean> {
  const wa = webApp();
  if (!wa?.shareMessage) return Promise.resolve(false);
  return new Promise((resolve) => wa.shareMessage!(msgId, (sent) => resolve(Boolean(sent))));
}

/** Открыть счёт Telegram Stars и дождаться результата. Вне Telegram оплата невозможна — 'failed'. */
export function openInvoice(url: string): Promise<InvoiceStatus> {
  const wa = webApp();
  if (!wa?.openInvoice) return Promise.resolve('failed');
  return new Promise((resolve) => wa.openInvoice!(url, (status) => resolve(status)));
}
